import { cloneDeep } from 'lodash-es';
import type { DigitalProductType, ThemeJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';

/**
 * Basic Digital V2 is identified by a stamp in the draft's own `cardJSON` rather than
 * by a product row, so enabling a theme stays a one-line edit to card_service's
 * hardcoded allowlist with no product to seed. The stamped value matches
 * `BASIC_DIGITAL_V2_DUMMY_PRODUCT.productType`, so there is one vocabulary for it
 * across the PDP, the gallery and the customizer.
 *
 * Why this round-trips without a schema change:
 * - card_service preserves unknown `cardJSON` keys -- `buildCardJSONForProductSwitch`
 *   spreads the existing object rather than rebuilding it.
 * - `useDraftSaver` persists the whole blob, so the stamp is saved with any edit.
 * - `DraftsGallery` already selects `cardJSON`, so a returning user's free draft is
 *   re-identified with no query change.
 *
 * The stamp is client-writable, which is safe because it only ever *restricts*:
 * faking it onto a paid draft downgrades your own editor, and stripping it off a free
 * draft drops you into the normal premium checkout. The entitlement boundary is
 * server-side, where card_service re-checks the theme against its allowlist.
 */
const BASIC_DIGITAL_V2 = 'basicDigitalV2' satisfies DigitalProductType;

type StampedCardJson = Pick<ThemeJson, 'digitalCustomizations'>;

/**
 * Anything unstamped is the paid premium product. Failing safe towards paid matters:
 * every draft created before this shipped is unstamped, and the `editDigitalOrder`
 * route synthesizes its config from the order rather than a draft.
 */
export const deriveDigitalProductType = (cardJSON: Maybe<StampedCardJson>): DigitalProductType => {
  return cardJSON?.digitalCustomizations?.productType === BASIC_DIGITAL_V2 ? BASIC_DIGITAL_V2 : 'digitalCard';
};

export const isBasicDigitalV2Draft = (cardJSON: Maybe<StampedCardJson>): boolean => {
  return deriveDigitalProductType(cardJSON) === BASIC_DIGITAL_V2;
};

/**
 * Returns a stamped copy of a template's `themeJSON`, for the two Basic Digital V2
 * draft creators.
 *
 * Always clones, and that is the point of the helper: the public-shop creator hands
 * `template.themeJSON` straight to the mutation, so stamping in place would mutate the
 * template object shared with the Apollo cache and the gallery.
 */
export const withBasicDigitalV2Stamp = (themeJSON: ThemeJson): ThemeJson => {
  const stamped = cloneDeep(themeJSON);
  stamped.digitalCustomizations = { ...stamped.digitalCustomizations, productType: BASIC_DIGITAL_V2 };
  return stamped;
};
